/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, October 26th 2021, 11:28:25 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useController } from "react-hook-form";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { OSFormInputProps } from "ui-widgets/forms/types";

interface UseInputProps<T> extends Pick<OSFormInputProps<T>, "schema_field_key"> {
  [key: string]: any;
}

export const useUncontrolledInput = <T>(props: UseInputProps<T>) => {
  const { fieldIsRequired, ...useFormReturn } = useOSFormContext<T>();

  const {
    register,
    formState: { errors },
  } = useFormReturn;

  const uncontrolledProps = {
    ...register(props.schema_field_key as any),
    required: fieldIsRequired(props.schema_field_key),
    error: errors?.[props.schema_field_key as any],
  };

  return { uncontrolledProps, useFormReturn };
};

export const useControlledInput = <T>(props: UseInputProps<T>) => {
  const { fieldIsRequired, ...useFormReturn } = useOSFormContext<T>();

  const { control } = useFormReturn;

  const { field, fieldState, formState } = useController({
    name: props.schema_field_key as any,
    control: control,
    rules: { required: fieldIsRequired(props.schema_field_key) },
    defaultValue: props.defaultValue,
  });

  const controlledProps = {
    required: fieldIsRequired(props.schema_field_key),
    error: formState.errors?.[props.schema_field_key as any],
  };

  return {
    field,
    fieldState,
    formState,
    controlledProps,
    useFormReturn,
  };
};
